import React, { FC, useState, useEffect } from 'react';
import { Box, List, ListItem, ListItemText, Grid } from '@mui/material';
import { ConfirmButton } from '@components';
import { useAppDispatch, useAppSelector } from '@hooks/redux-custom';
import { CustomerAddress, CustomerAddressFormFields, JimboResponse } from '@models';
import { useDeleteCustomerAddressMutation } from '@services';
import { deleteSelectedCustomerAddress } from '@store/slices/customer';
import { addErrorToLog } from '@store/slices/error-log';
import { showToast } from '@store/slices/notification';

type Props = {
  address: CustomerAddress;
};

interface GeoFields {
  lat: string | number | string[];
  lng: string | number | string[];
  gnafpid: string | number | string[];
}

export const AddressView: FC<Props> = ({ address }) => {
  const [deleteCustomerAddress, { isLoading }] = useDeleteCustomerAddressMutation();
  const [geoFields, setGeoFields] = useState<GeoFields>({ lat: '', lng: '', gnafpid: '' });

  const activeUser = useAppSelector((state) => state.activeUser.value);

  const userAccessStatus = activeUser?.user?.roles.find((role: any) => role.moduleName === 'customer');
  const hasWriteAccess = userAccessStatus.role > 1;

  const dispatch = useAppDispatch();

  const deleteAddress = async () => {
    deleteCustomerAddress(address.id)
      .unwrap()
      .then(() => {
        dispatch(
          showToast({
            message: 'Address successfully deleted!',
            type: 'success',
          }),
        );

        dispatch(deleteSelectedCustomerAddress(address.id));
      })
      .catch((error: { data: JimboResponse }) => {
        dispatch(addErrorToLog(error?.data));

        dispatch(
          showToast({
            message: 'Failed to delete the address!',
            type: 'error',
          }),
        );
      });
  };

  useEffect(() => {
    if (address.form_fields?.length) {
      setGeoFields({
        lat: address.form_fields.find((field) => field.name === CustomerAddressFormFields.Lat)?.value ?? '',
        lng: address.form_fields.find((field) => field.name === CustomerAddressFormFields.Long)?.value ?? '',
        gnafpid: address.form_fields.find((field) => field.name === CustomerAddressFormFields.GNAFPID)?.value ?? '',
      });
    }
  }, [address]);

  return (
    <>
      <List data-testid="address-view">
        <Grid container spacing={1}>
          <Grid item xs={4}>
            <ListItem>
              <ListItemText
                primaryTypographyProps={{ variant: 'caption' }}
                primary="Subdwelling"
                secondaryTypographyProps={{ variant: 'subtitle1' }}
                secondary={address.address2}
                data-testid="address-view-address2"
              />
            </ListItem>
          </Grid>
          {address.address2 && (
            <Grid item xs={4}>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: 'caption' }}
                  primary="Street Address"
                  secondaryTypographyProps={{ variant: 'subtitle1' }}
                  secondary={address.address1}
                  data-testid="address-view-address1"
                />
              </ListItem>
            </Grid>
          )}
          {address.postal_code && (
            <Grid item xs={4}>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: 'caption' }}
                  primary="Postal Code"
                  secondaryTypographyProps={{ variant: 'subtitle1' }}
                  secondary={address.postal_code}
                  data-testid="address-view-postal-code"
                />
              </ListItem>
            </Grid>
          )}
          {address.city && (
            <Grid item xs={4}>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: 'caption' }}
                  primary="City or Suburb"
                  secondaryTypographyProps={{ variant: 'subtitle1' }}
                  secondary={address.city}
                  data-testid="address-view-city-suburb"
                />
              </ListItem>
            </Grid>
          )}
          {address.state_or_province && (
            <Grid item xs={4}>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: 'caption' }}
                  primary="State"
                  secondaryTypographyProps={{ variant: 'subtitle1' }}
                  secondary={address.state_or_province}
                  data-testid="address-view-state"
                />
              </ListItem>
            </Grid>
          )}
          {address.country && (
            <Grid item xs={4}>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: 'caption' }}
                  primary="Country"
                  secondaryTypographyProps={{ variant: 'subtitle1' }}
                  secondary={address.country}
                  data-testid="address-view-country"
                />
              </ListItem>
            </Grid>
          )}
          {geoFields.lat !== '' && (
            <Grid item xs={4}>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: 'caption' }}
                  primary="Latitude"
                  secondaryTypographyProps={{ variant: 'subtitle1' }}
                  secondary={geoFields.lat}
                  data-testid="address-view-latitude"
                />
              </ListItem>
            </Grid>
          )}
          {geoFields.lng !== '' && (
            <Grid item xs={4}>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: 'caption' }}
                  primary="Longitude"
                  secondaryTypographyProps={{ variant: 'subtitle1' }}
                  secondary={geoFields.lng}
                  data-testid="address-view-longitude"
                />
              </ListItem>
            </Grid>
          )}
          {geoFields.gnafpid !== '' && (
            <Grid item xs={4}>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: 'caption' }}
                  primary="GNAFPID"
                  secondaryTypographyProps={{ variant: 'subtitle1' }}
                  secondary={geoFields.gnafpid}
                  data-testid="address-view-gnafpid"
                />
              </ListItem>
            </Grid>
          )}
          {address.country && (
            <Grid item xs={4}>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: 'caption' }}
                  primary="Country"
                  secondaryTypographyProps={{ variant: 'subtitle1' }}
                  secondary={address.country}
                  data-testid="address-view-country"
                />
              </ListItem>
            </Grid>
          )}

          {address.address_type && (
            <Grid item xs={4}>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: 'caption' }}
                  primary="Address Type"
                  secondaryTypographyProps={{ variant: 'subtitle1' }}
                  secondary={address.address_type[0].toUpperCase() + address.address_type.substring(1)}
                  data-testid="address-view-type"
                />
              </ListItem>
            </Grid>
          )}
          {address.company && (
            <Grid item xs={4}>
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: 'caption' }}
                  primary="Company Name"
                  secondaryTypographyProps={{ variant: 'subtitle1' }}
                  secondary={address.company}
                  data-testid="address-view-company"
                />
              </ListItem>
            </Grid>
          )}
        </Grid>
      </List>
      {hasWriteAccess && (
        <Box display="flex" justifyContent="space-between" data-testid={`address-view-buttons-${address.postal_code}`}>
          <ConfirmButton isLoading={isLoading} onClick={deleteAddress} color="error" text="delete" confirmationText="are you sure?" />
        </Box>
      )}
    </>
  );
};
